import type { ThemeConfig } from "antd";

/**
 * Canonical antd theme for the PAM admin.
 *
 * Both `index.js` (production) and `.storybook/preview.ts` import this so the
 * antd component tokens stay in sync between the running app and Storybook.
 *
 * All color values point at the dynamic `--COLOR-*` palette injected by
 * `applyColorVariables` (`src/utils/colorUtils.js`). When updating, prefer
 * adding tokens here over per-component CSS overrides — antd applies these
 * universally to every consumer, including third-party imports of antd.
 */
const antdTheme: ThemeConfig = {
  components: {
    Skeleton: {
      gradientFromColor: "var(--grey-750)",
      gradientToColor: "var(--grey-800)",
    },
    Tabs: {
      itemColor: "var(--COLOR-LIGHT_TEXT_COLOR)",
      itemSelectedColor: "var(--COLOR-LIGHT_TEXT_COLOR)",
      itemHoverColor: "var(--COLOR-LIGHT_TEXT_COLOR)",
      itemActiveColor: "none",
      inkBarColor: "none",
    },
    DatePicker: {
      activeBg: "var(--COLOR-BETCARD_HEADER_BG)",
      activeBorderColor: "transparent",
      hoverBorderColor: "var(--COLOR-PRIMARY)",
      cellHoverWithRangeBg: "var(--COLOR-BORDER_AND_GRADIENT_BG)",
      colorBgContainer: "var(--COLOR-BETCARD_HEADER_BG)",
      colorBorder: "transparent",
      colorText: "var(--COLOR-LIGHT_TEXT_COLOR)",
      colorTextHeading: "var(--COLOR-LIGHT_TEXT_COLOR)",
      colorBgElevated: "var(--COLOR-BETCARD_HEADER_BG)",
      colorIcon: "var(--COLOR-LIGHT_TEXT_COLOR)",
      cellActiveWithRangeBg: "var(--COLOR-BORDER_AND_GRADIENT_BG)",
      controlItemBgActive: "var(--COLOR-BORDER_AND_GRADIENT_BG)",
      colorPrimary: "var(--COLOR-PRIMARY)",
      colorIconHover: "var(--COLOR-BORDER_AND_GRADIENT_BG)",
      colorTextDisabled: "var(--COLOR-LIGHT_TEXT_COLOR)",
    },
    Table: {
      headerBg: "var(--COLOR-BETCARD_HEADER_BG)",
      headerColor: "var(--COLOR-TEXT_INPUT_PLACEHOLDER_TEXT)",
      headerSplitColor: "transparent",
      borderColor: "var(--COLOR-BETCARD_HEADER_BG)",
      colorBgContainer: "var(--COLOR-DEFAULT_BORDER_GRADIENT_0)",
      colorText: "var(--COLOR-LIGHT_TEXT_COLOR)",
      fontFamily: "Poppins-regular",
    },
    Pagination: {
      colorPrimary: "var(--COLOR-PRIMARY)",
      colorPrimaryHover: "var(--COLOR-PRIMARY)",
      colorPrimaryBorder: "var(--COLOR-PRIMARY)",
      colorText: "var(--COLOR-LIGHT_TEXT_COLOR)",
      itemActiveBg: "transparent",
      colorTextDisabled: "var(--COLOR-TEXT_INPUT_PLACEHOLDER_TEXT)",
      colorBgTextHover: "var(--COLOR-PRIMARY)",
    },
    Input: {
      colorText: "var(--COLOR-LIGHT_TEXT_COLOR)",
      activeBorderColor: "transparent",
      colorPrimaryHover: "transparent",
      colorBgContainer: "#25252F",
      colorBorder: "transparent",
    },
    Select: {
      colorBgContainer: "transparent",
      colorText: "var(--COLOR-TEXT_INPUT_PLACEHOLDER_TEXT)",
      colorPrimary: "var(--COLOR-PRIMARY)",
      optionActiveBg: "var(--COLOR-DEFAULT_BORDER_GRADIENT_0)",
      colorBgElevated: "var(--COLOR-BETCARD_HEADER_BG)",
      colorTextPlaceholder: "var(--COLOR-TEXT_INPUT_PLACEHOLDER_TEXT)",
    },
    // Dropdown menus previously fell through to antd's light defaults —
    // the only elevated surface that did. Panel/hover mirror the select
    // popup (`.ant-select-dropdown` in App.css / Select.optionActiveBg).
    Dropdown: {
      colorBgElevated: "var(--grey-600)",
      colorText: "var(--COLOR-LIGHT_TEXT_COLOR)",
      controlItemBgHover: "var(--COLOR-DEFAULT_BORDER_GRADIENT_0)",
      colorSplit: "var(--grey-700)",
    },
    Modal: {
      colorBgContainer: "var(--COLOR-MODAL_BACKGROUND)",
      colorText: "var(--COLOR-LIGHT_TEXT_COLOR)",
      colorTextHeading: "var(--COLOR-LIGHT_TEXT_COLOR)",
      colorBgElevated: "var(--COLOR-MODAL_BACKGROUND)",
      colorBorder: "transparent",
      controlItemBgActive: "var(--COLOR-BORDER_AND_GRADIENT_BG)",
    },
    Drawer: {
      colorBgContainer: "var(--COLOR-MODAL_BACKGROUND)",
      colorText: "var(--COLOR-LIGHT_TEXT_COLOR)",
      colorTextHeading: "var(--COLOR-LIGHT_TEXT_COLOR)",
      colorBgElevated: "var(--COLOR-MODAL_BACKGROUND)",
      colorBorder: "transparent",
    },
    Popover: {
      colorBgContainer: "var(--COLOR-MODAL_BACKGROUND)",
      colorText: "var(--COLOR-LIGHT_TEXT_COLOR)",
      colorTextHeading: "var(--COLOR-LIGHT_TEXT_COLOR)",
      colorBgElevated: "var(--COLOR-MODAL_BACKGROUND)",
      colorBorder: "transparent",
    },
  },
};

/**
 * antd `<Form>` defaults applied at the `<ConfigProvider form={...}>` level.
 *
 * `requiredMark: false` hides the red `*` next to required field labels — we
 * use field-level error messages and validation rules instead. Override per
 * form with `<Form requiredMark>` if a particular form needs the indicator.
 */
export const antdFormConfig = {
  requiredMark: false as const,
};

export default antdTheme;
